import * as Logger from './logger.js';
import { evaluate } from './utils.js';

export class HSWorkflow {
    id;
    widget;
    drawCardFunc;
    workflow = {};      // the configuration for the workflow
    config = {};        // the current configuration options for this card
    abortController;    // used to abort event listeners and any fetch activity when closing
    configs = [];       // stack of config snapshots
    states = [];        // stack of state snapshots which are merged to define our card config
    step = 0;           // the current step of the workflow progression

    constructor(id, workflow, drawCardFunc) { 
        this.id = id;
        this.workflow = workflow;
        this.drawCardFunc = drawCardFunc;
        const path = workflow.default_path;
        this.config = workflow[path];
        this.abortController = new AbortController();
    }

    init(widget) {
        this.widget = widget;
        this.loadCard();
    }

    getCardConfig() {
        return this.config;
    }

    loadCard() {
        const config = evaluate(this.config, [ 'state' ], [ this.getMergedState() ]);
        const card = this.drawCardFunc(this.widget, this.id, config);
        Logger.debug("card config: ", config);

        // setup card workflow action buttons
        // NOTE: we attach the handlers to the buttons using the card's onEvent function so that
        // these get cleaned up automatically by the abort when that card is removed
        const prevButton = this.widget.querySelector('#prev-button');
        if (this.step > 0) {
            prevButton.classList.remove('hide');
            card.onEvent('click', () => this.prev(), prevButton);
        } else {
            prevButton.classList.add('hide');
        }
        const applyButton = this.widget.querySelector('#apply-button');
        if (config.onapply) {
            applyButton.classList.remove('hide');
            card.onEvent('click', () => card.runAction(config.onapply), applyButton);
        } else {
            applyButton.classList.add('hide');
        }
        const nextButton = this.widget.querySelector('#next-button');
        if (config.onnext) {
            nextButton.classList.remove('hide');
            card.onEvent('click', () => card.runAction(config.onnext), nextButton);
        } else {
            nextButton.classList.add('hide');
        }

        card.setActionHandler('prev', this.prev.bind(this));
        card.setActionHandler('next', this.next.bind(this));
        card.setActionHandler('apply', this.apply.bind(this));
    }

    //
    // pop the current workflow state and config and move back to the previous step of the workflow
    //
    prev() {
        if (this.step > 0) {
            this.abort();
            this.pop();
            this.loadCard();
        }
    }

    //
    // save the current workflow state onto the cardState stack and move to the next step of the workflow
    //
    next(opts) {
        this.abort();
        this.pushState(opts.state);
        let config = structuredClone(opts.path.split('.').reduce((accum, key) => accum[key], this.workflow));
        this.pushConfig(config);
        this.loadCard();
    }

    //
    // merge the current workflow state onto the cardState stack and move back to the previous step of the workflow
    //
    apply(opts) {
        this.abort();
        this.applyState(opts.state);
        this.loadCard();
    }

    pushState(state) {
        this.states.push(state);
        this.step++;
    }

    applyState(state) {
        // pop off the empty state for this transitional step, then merge our new
        // state into the previous step state and save on the stack
        this.states.pop();  
        const prev_state = this.states.pop();
        this.states.push({ ...prev_state, ...state });
        this.config = this.configs.pop();
        this.step--;
    }

    pushConfig(config) {
        this.configs.push(this.config);
        this.config = config;
    }

    pop() {
        this.states.pop();
        this.config = this.configs.pop();
        this.step--;
    }

    getMergedState() {
        return this.states.reduce((mergedState,state) => ({ ...mergedState, ...state }), {});
    }

    signal() {
        return this.abortController.signal;
    }

    // remove all event listeners and cancel any ongoing fetches for this card
    abort() {
        this.abortController.abort();
        // once an AbortController is used, it can't be reset, so we must allocate a new one
        // to be used for any subsequent card activity
        this.abortController = new AbortController();
    }
}
